<script setup lang="ts">
import MarketingFlowPreview from '@/views/autopilot/Components/MarketingFlowPreview.vue'
import MarketingFlowCreationStepsLoader from '@/views/autopilot/Components/MarketingFlowCreationStepsLoader.vue'
import AudienceConfirmationModal from '@/views/autopilot/Components/AudienceConfirmationModal.vue'

import { useMarketingFlowTemplates } from '@/composables/marketingFlowsTemplates'
import { useMarketingFlowWithAudience } from '@/composables/marketingFlowWithAudience'
import type { Template } from '@/views/autopilot/config/templates.config'
import type { Audience } from '@/types/types'

const props = defineProps<{
  templates: Template[]
}>()

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { triggerSettings, delaySettings, actionSettings, setTemplate, resetTemplate } = useMarketingFlowTemplates()
const {
  currentCreationStep,
  creationError,
  getAudienceTemplateConfig,
  createMarketingFlowWithAudience,
  resetCreationState
} = useMarketingFlowWithAudience()
const { fetchAudienceById, fetchAudienceByTemplate } = useAudience()
const { send } = useEvent()

const audiences = ref<Audience[]>([])
const isModalOpen = ref(false)
const isConfirmationModalOpen = ref(false)
const isLoaderModalOpen = ref(false)
const selectedTemplate = ref<Template>()
const createdAudienceId = ref<number | null>(null)
const existingAudienceId = ref<number | null>(null)

const requiresAudience = computed(() => !!selectedTemplate.value?.requiresAudienceTemplate)

const audienceTemplateInfo = computed(() => {
  if (!selectedTemplate.value?.requiresAudienceTemplate) return null
  return getAudienceTemplateConfig(selectedTemplate.value.requiresAudienceTemplate)
})

const audienceName = computed(() => {
  if (!audienceTemplateInfo.value) return ''
  return t(audienceTemplateInfo.value.title)
})

const prerequisiteSectionTitle = computed(() => {
  if (!selectedTemplate.value) return ''

  if (selectedTemplate.value.audienceCreationType === 'auto') {
    return t('pages.autopilot.autopilotsPage.templates.sections.readyToUse')
  }

  return t('pages.autopilot.autopilotsPage.templates.sections.actionsRequired')
})

const audienceRules = computed(() => {
  if (!audienceTemplateInfo.value) return undefined
  return audienceTemplateInfo.value.rules
})

const selectAudience = async () => {
  if (triggerSettings.value?.selectedAudience) {
    try {
      const audience = await fetchAudienceById(triggerSettings.value?.selectedAudience)
      audiences.value = [audience]
    } catch {
      audiences.value = []
    }
  }
}

const syncTemplateQuery = (type: Template['type']) => {
  const query = { ...route.query }
  if (type) {
    query.template = type
  } else {
    delete query.template
  }
  router.replace({ query })
}

const openModal = async (template: Template, options: { track?: boolean } = {}) => {
  if (options.track !== false) {
    send('segment', 'automation.template.card_clicked', {
      template_name: template.type
    })
  }

  if (template.type) {
    try {
      selectedTemplate.value = template

      let audienceId: number | undefined
      if (template.requiresAudienceTemplate) {
        const existingAudiences = await fetchAudienceByTemplate(template.requiresAudienceTemplate)
        if (existingAudiences && existingAudiences.length > 0) {
          audiences.value = existingAudiences
          existingAudienceId.value = existingAudiences[0].id
          audienceId = existingAudiences[0].id
        }
      }

      await Promise.all([setTemplate(template.type, audienceId), selectAudience()])
      isModalOpen.value = true
      syncTemplateQuery(template.type)
    } catch {
      audiences.value = []
    }
  }
}

const handleCreate = async () => {
  if (!selectedTemplate.value) return

  send('segment', 'automation.template.modal_used', {
    template_name: selectedTemplate.value.type
  })

  if (selectedTemplate.value.requiresAudienceTemplate) {
    if (existingAudienceId.value) {
      router.push({
        name: 'MarketingFlowCreate',
        query: {
          fromTemplate: selectedTemplate.value.type,
          audienceId: existingAudienceId.value.toString()
        }
      })

      return
    }

    if (selectedTemplate.value.audienceCreationType === 'edit') {
      isModalOpen.value = false
      isConfirmationModalOpen.value = true

      return
    }

    if (selectedTemplate.value.audienceCreationType === 'auto') {
      isModalOpen.value = false
      await handleConfirmCreation()

      return
    }
  }

  router.push({
    name: 'MarketingFlowCreate',
    query: {
      fromTemplate: selectedTemplate.value.type
    }
  })
}

const handleConfirmCreation = async () => {
  if (!selectedTemplate.value) return

  try {
    isConfirmationModalOpen.value = false
    isLoaderModalOpen.value = true

    const result = await createMarketingFlowWithAudience(selectedTemplate.value, audienceName.value)

    if (result.success && result.audienceId) {
      createdAudienceId.value = result.audienceId

      isLoaderModalOpen.value = false

      router.push({
        name: 'MarketingFlowCreate',
        query: {
          fromTemplate: selectedTemplate.value.type,
          audienceId: result.audienceId.toString()
        }
      })
    }
  } catch (error) {
    console.error('Failed to create marketing flow with audience:', error)
    isLoaderModalOpen.value = false
  }
}

const handleClose = () => {
  if (selectedTemplate.value) {
    send('segment', 'automation.template.preview_closed', {
      template_name: selectedTemplate.value.type
    })
  }

  selectedTemplate.value = null
  isModalOpen.value = false
  isConfirmationModalOpen.value = false
  isLoaderModalOpen.value = false
  createdAudienceId.value = null
  existingAudienceId.value = null
  resetTemplate()
  resetCreationState()
  audiences.value = []
  syncTemplateQuery(null)
}

const handleCloseConfirmation = () => {
  isConfirmationModalOpen.value = false
  isModalOpen.value = true
}

const handleEditAudience = () => {
  if (!selectedTemplate.value?.requiresAudienceTemplate) return

  const audienceTemplateConfig = getAudienceTemplateConfig(selectedTemplate.value.requiresAudienceTemplate)
  if (!audienceTemplateConfig) return

  router.push({
    name: 'AudienceCreate',
    query: {
      name: t(audienceTemplateConfig.title),
      template: selectedTemplate.value.requiresAudienceTemplate,
      fromMarketingFlow: selectedTemplate.value.type
    }
  })
}

const handleReturnFromAudienceCreate = async () => {
  const fromAudienceCreate = route.query.fromAudienceCreate as string | undefined
  const audienceId = route.query.audienceId as string | undefined
  const marketingFlowTemplate = route.query.marketingFlowTemplate as string | undefined

  if (!fromAudienceCreate || !audienceId || !marketingFlowTemplate) {
    return
  }

  try {
    const template = props.templates.find((t) => t.type === marketingFlowTemplate)
    if (!template) {
      console.error('Template not found:', marketingFlowTemplate)
      return
    }

    selectedTemplate.value = template
    await setTemplate(template.type)

    isLoaderModalOpen.value = true
    currentCreationStep.value = 'creating'

    await new Promise((resolve) => setTimeout(resolve, 500))
    currentCreationStep.value = 'completed'

    await new Promise((resolve) => setTimeout(resolve, 2000))

    router.replace({
      name: 'MarketingFlowCreate',
      query: {
        fromTemplate: template.type,
        audienceId: audienceId
      }
    })
  } catch (error) {
    console.error('Error handling return from audience create:', error)
  }
}

const openFromQuery = () => {
  const templateType = route.query.template as string | undefined
  if (!templateType || route.query.fromAudienceCreate) return

  const template = props.templates.find((t) => t.type === templateType)
  if (template) {
    openModal(template, { track: false })
  }
}

onMounted(() => {
  handleReturnFromAudienceCreate()
  openFromQuery()
})
</script>

<template>
  <BaseTemplateCard
    v-for="template in templates"
    :key="template.id"
    :icon="template.icon"
    :category="template.category"
    :title="template.title"
    :description="template.description"
    :recommended-text="t('global.recommended')"
    :button-text="t('pages.autopilot.autopilotsPage.templates.page.callToAction')"
    :is-recommended="template.isRecommended"
    show-time
    class="h-full"
    :time-value="template.time"
    :time-unit="t('global.time.min')"
    :data-testid="`marketing-flow-template-${template.type}`"
    @click="openModal(template)"
    @action="openModal(template)"
  />

  <BaseModal
    :is-open="isModalOpen"
    :heading-title="selectedTemplate ? selectedTemplate.title : ''"
    :link-text="t('pages.autopilot.autopilotsPage.templates.page.useTemplate')"
    size="6xl"
    :cancel="t('global.words.cancel')"
    @click="handleCreate"
    @close="handleClose"
  >
    <div v-if="selectedTemplate" class="flex flex-col">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12 order-2 md:order-1 md:col-span-6 bg-content-bg py-8 px-5">
          <div class="dotted-background flex h-full flex-col items-center">
            <MarketingFlowPreview
              :trigger-settings="triggerSettings"
              :delay-settings="delaySettings"
              :action-settings="actionSettings"
              :audiences="audiences"
              :stores="[]"
              :rewards="[]"
              :disabled-card-click="{
                trigger: true,
                delay: true,
                action: true
              }"
              :step-has-error="{
                trigger: false,
                delay: false,
                action: false
              }"
              :show-stats="false"
              :templateAudienceName="audienceName"
              @trigger-card-clicked="() => {}"
              @delay-card-clicked="() => {}"
              @action-card-clicked="() => {}"
            />
          </div>
        </div>
        <div class="col-span-12 order-1 md:order-2 md:col-span-6 space-y-4 p-5">
          <div class="space-y-2">
            <h3 class="text-base font-semibold text-foreground uppercase">
              {{ t('pages.autopilot.autopilotsPage.templates.sections.about') }}
            </h3>
            <p class="text-base font-normal text-foreground-secondary leading-6">
              {{ selectedTemplate.description }}
            </p>
          </div>
          <div class="flex flex-col gap-2.5">
            <h3 class="text-base font-semibold text-foreground uppercase">
              {{ t('pages.autopilot.autopilotsPage.templates.sections.details') }}
            </h3>
            <div class="text-base font-normal text-foreground-secondary leading-6">
              <div v-for="(detail, index) in selectedTemplate.details" :key="detail.title">
                <p v-if="detail.title" :class="{ 'mt-6 font-semibold': index === 1 }">{{ detail.title }}</p>
                <ul class="list-disc list-inside">
                  <li>{{ detail.desc_1 }}</li>
                  <li v-if="detail.desc_2">{{ detail.desc_2 }}</li>
                  <li v-if="detail.desc_3">{{ detail.desc_3 }}</li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="requiresAudience && audienceTemplateInfo && !existingAudienceId" class="flex flex-col gap-2.5">
            <h3 class="text-base font-semibold text-foreground uppercase">
              {{ prerequisiteSectionTitle }}
            </h3>
            <div class="border border-border rounded-lg p-2.5 flex items-center gap-3">
              <div class="bg-muted p-2 rounded-xl shrink-0">
                <BaseIcon :name="selectedTemplate.icon" class="h-5 w-5 text-foreground-secondary" />
              </div>
              <p class="text-sm font-medium text-foreground leading-5">
                {{
                  t('pages.autopilot.autopilotsPage.templates.prerequisite.autoCreate', {
                    audienceName: t(audienceTemplateInfo.title)
                  })
                }}
              </p>
            </div>
          </div>

          <BaseTip
            v-if="selectedTemplate.tip"
            :text="selectedTemplate.tip"
            severity="warn"
            :icon-name="selectedTemplate.icon"
          />
        </div>
      </div>
    </div>
  </BaseModal>

  <AudienceConfirmationModal
    v-if="selectedTemplate"
    :is-open="isConfirmationModalOpen"
    :icon="selectedTemplate.icon"
    :audience-name="audienceName"
    :audience-rules="audienceRules"
    :template-title="selectedTemplate.title"
    @close="handleCloseConfirmation"
    @confirm="handleConfirmCreation"
    @edit="handleEditAudience"
  />

  <BaseModal :is-open="isLoaderModalOpen" :heading-title="''" size="md" :show-footer="false" @close="() => {}">
    <MarketingFlowCreationStepsLoader
      v-if="selectedTemplate"
      :current-step="currentCreationStep"
      :icon="selectedTemplate.icon"
      :audience-name="audienceName"
    />

    <div v-if="creationError" class="mt-4">
      <BaseTip :text="creationError" severity="error" />
    </div>
  </BaseModal>
</template>
